import { semiBoldFontWeight } from '@globals/themes/themeConstants'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Collapse from '@mui/material/Collapse'
import Divider from '@mui/material/Divider'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { SxProps, Theme, styled } from '@mui/material/styles'
import { T } from '@transifex/react'
import { MouseEvent } from 'react'
import ClearAction from '../ClearAction'
import { Selectable, SelectionCheckbox } from '../selections'
import { filterListItemStyle } from '../styles'

/**
 * The popup, rather than the list inside it, owns the height: that way the list
 * can take over the space the applied filters give up as they collapse. The
 * value matches the max height MUI gives an Autocomplete listbox by default.
 */
export const POPUP_MAX_HEIGHT_VH = 40

/** A filter popup: a column of fixed rows around one scrolling list. */
export const filterPopupSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  maxHeight: `${POPUP_MAX_HEIGHT_VH}vh`,
  overflow: 'hidden',
}

/**
 * The one scrolling element of a filter popup. It has to be the only one, or
 * collapsing the applied filters would shift the rows under the pointer.
 */
export const filterListSx: SxProps<Theme> = { flex: 1, minHeight: 0, maxHeight: 'none' }

/** Anything in a filter popup that is not the scrolling list. */
export const filterStaticSx: SxProps<Theme> = { flexShrink: 0 }

type AppliedFiltersProps = {
  items: Selectable[]
  /** Whether the list has taken this space over — see `useAppliedFiltersCollapse`. */
  hidden: boolean
  onClear: () => void
  onDeselect: (item: Selectable) => void
  clearId: string
  /**
   * Horizontal padding of the rows, so they line up with the rows of the list
   * they sit above. Options in `DynamicSelect` take MUI's default list padding;
   * the team filter's rows are tighter than that.
   */
  rowPaddingX?: number
}

/**
 * A filter's selections, pinned above the list they were chosen from. They slide
 * out of the way as the user scrolls into the list and return on the first
 * upward scroll, which is what `hidden` carries.
 */
export const AppliedFilters = ({
  items,
  hidden,
  onClear,
  onDeselect,
  clearId,
  rowPaddingX = 2,
}: AppliedFiltersProps) => {
  if (items.length === 0) return null

  return (
    <Collapse in={!hidden} sx={filterStaticSx}>
      {/* A listbox stops mousedown from blurring the input for its own rows.
          These rows sit outside it and need the same guard, or the popup closes
          before the click lands. */}
      <Box onMouseDown={preventInputBlur} sx={sectionSx}>
        <Stack
          direction="row"
          sx={{
            justifyContent: 'space-between',
            px: rowPaddingX,
            py: 2,
            flexShrink: 0,
          }}>
          <Typography
            component="div"
            variant="caption"
            sx={{
              fontWeight: semiBoldFontWeight,
            }}>
            <T _str="Applied Filters" />
          </Typography>
          <ClearAction id={clearId} onClick={onClear}>
            <Typography
              component="span"
              variant="caption"
              sx={{
                fontWeight: semiBoldFontWeight,
                color: 'inherit',
              }}>
              <T _str="Clear all" />
            </Typography>
          </ClearAction>
        </Stack>
        <List dense disablePadding sx={listSx}>
          {items.map(item => (
            <ListItem key={item.id} disablePadding>
              <ListItemButton
                onClick={() => onDeselect(item)}
                sx={{ ...filterListItemStyle, py: 0.75, px: rowPaddingX }}>
                <SelectionCheckbox selected />
                <TruncatedLabel>{item.name}</TruncatedLabel>
              </ListItemButton>
            </ListItem>
          ))}
        </List>
        <Divider sx={{ mb: 1, mt: 1, flexShrink: 0 }} />
      </Box>
    </Collapse>
  )
}

/** The foot of a list that is fetching its next batch. */
export const LoadingMore = () => (
  <Box sx={loadingMoreSx}>
    <CircularProgress color="inherit" size={20} />
  </Box>
)

const preventInputBlur = (event: MouseEvent) => event.preventDefault()

// However many filters are applied, they take at most this much of the popup and
// scroll within it, so the list they were chosen from always keeps the rest.
const sectionSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  maxHeight: `${POPUP_MAX_HEIGHT_VH * 0.6}vh`,
}

// Shrinking below the rows' own height is what lets this scroll rather than
// pushing the heading and the list out of the popup.
const listSx: SxProps<Theme> = { minHeight: 0, overflowY: 'auto' }

const loadingMoreSx: SxProps<Theme> = {
  display: 'flex',
  flexShrink: 0,
  justifyContent: 'center',
  padding: 1,
}

const TruncatedLabel = styled('span')({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
})
